import React from 'react';
import DOMPurify from 'dompurify';
import Heading from '@atlaskit/heading';
import { Box, Stack, xcss } from '@atlaskit/primitives';
import SectionMessage from '@atlaskit/section-message';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import type { AlertDTO } from '../../../../common/types';
import { AccessibleAnchor } from '../../../../common/ui/accessible-anchor/index.tsx';
import messages from './messages';

const AlertListItem = ({ alert }: { alert: AlertDTO }) => {
	const { formatMessage } = useIntl();

	if (alert.templateParams.link && alert.templateName === 'missingPermission') {
		return (
			<li>
				<Stack space="space.025">
					<Heading size="xsmall">{alert.summary}</Heading>
					<Box
						dangerouslySetInnerHTML={{
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							__html: DOMPurify.sanitize(alert.templateParams.description) as TrustedHTML,
						}}
					/>
					<AccessibleAnchor href={alert.templateParams.link}>
						{formatMessage(messages.missionPermissionErrorLearnLink)}
					</AccessibleAnchor>
					<Box>
						<FormattedMessage
							{...messages.missionPermissionErrorSupportLink}
							values={{
								a: (chunks) => (
									<AccessibleAnchor href="https://support.atlassian.com/contact/#/" target="_blank">
										{chunks}
									</AccessibleAnchor>
								),
							}}
						/>
					</Box>
				</Stack>
			</li>
		);
	}
	if (alert.templateParams.link && alert.templateName === 'AnonymousAccessAllowed') {
		return (
			<li>
				<Stack space="space.025">
					<Heading size="xsmall">{formatMessage(messages.anonymousAccessErrorSummary)}</Heading>
					<AccessibleAnchor href="https://confluence.atlassian.com/servicedeskcloud/blog/2017/04/introducing-the-login-free-portal-for-jira-service-desk-cloud">
						{formatMessage(messages.anonymousAccessErrorDescription)}
					</AccessibleAnchor>
				</Stack>
			</li>
		);
	}
	return (
		<li>
			<Heading size="xsmall">{alert.summary}</Heading>
			<br />
			{alert.templateParams.link ? (
				<AccessibleAnchor href={alert.templateParams.link}>
					{alert.templateParams.description}
				</AccessibleAnchor>
			) : (
				alert.templateParams.description
			)}
		</li>
	);
};

const PreconditionError = ({ alerts }: Props) => {
	const { formatMessage } = useIntl();

	return (
		<Box xcss={containerStyles}>
			<SectionMessage appearance="error" title={formatMessage(messages.title)}>
				<p>
					{formatMessage(messages.firstLine, {
						length: alerts.length,
					})}
				</p>
				<ul>
					{alerts.map((alert: AlertDTO, index) => (
						<AlertListItem alert={alert} key={index} />
					))}
				</ul>
				<p>
					{formatMessage(messages.lastLine, {
						length: alerts.length,
					})}
				</p>
			</SectionMessage>
		</Box>
	);
};

export default PreconditionError;

interface Props {
	alerts: AlertDTO[];
}

const containerStyles = xcss({
	marginTop: 'space.300',
});
